import * as Sentry from '@sentry/vue'
import type { App } from 'vue'
import type { Router } from 'vue-router'

/**
 * Scripts the dashboard shell loads beside the app. Their failures are not
 * RightMessage exceptions, and the registration Meta pixel is one of them.
 */
const THIRD_PARTY_DENY_URLS: RegExp[] = [
  /connect\.facebook\.net/i,
  /facebook\.net/i,
  /facebook\.com/i,
  /googletagmanager\.com/i,
  /google-analytics\.com/i,
  /googleadservices\.com/i,
  /cdn\.firstpromoter\.com/i,
  /static\.ferndesk\.com/i,
  /^chrome-extension:\/\//i,
  /^moz-extension:\/\//i,
  /^safari(?:-web)?-extension:\/\//i
]

const IGNORED_ERRORS: (string | RegExp)[] = [
  'Script error.',
  'ResizeObserver loop limit exceeded',
  'ResizeObserver loop completed with undelivered notifications.'
]

/**
 * Initializes browser Sentry delivery for production deployments with an explicit DSN.
 *
 * Tracing and replay are off. The organization span quota is exhausted
 * (`span_usage_exceeded`), and a pageload transaction to that quota returns
 * HTTP 429. Error events are a separate category and stay at the SDK default
 * of 100%. `/sentry-tunnel` is same-origin so ad blockers do not drop them.
 * `VITE_SENTRY_DSN` must be the javascript-vue project, not the Laravel DSN.
 */
export function initializeSentry(app: App<Element>, router: Router): void {
  const client = Sentry.getClient()
  if (client?.getOptions().enabled === true) {
    return
  }

  Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    release: window.__RIGHTMESSAGE_RELEASE_COMMIT__ ?? undefined,
    tunnel: '/sentry-tunnel',
    integrations: [Sentry.browserTracingIntegration({ router }), Sentry.replayIntegration()],
    tracesSampleRate: 0,
    tracePropagationTargets: ['new.rightmessage.com'],
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    ignoreErrors: IGNORED_ERRORS,
    denyUrls: THIRD_PARTY_DENY_URLS,
    enabled: import.meta.env.PROD && Boolean(import.meta.env.VITE_SENTRY_DSN)
  })
}
